// @ts-nocheck

import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';

import BetslipOptionsModalContainer from '../../modules/betslip-options/components/BetslipOptionsModal/BetslipOptionsModalContainer';
import { BetslipEntity } from '../../states/entities/betslips';
import theme from '../../styled-components/dark';
import { type CardMode } from '../../types/common';
import { BetslipStatus } from '../../types/history';
import { SportIds } from '../../types/sports';
import SportIcon from '../SportIcon';

const MAX_BETSLIP_DETAIL = 3;

const getColorFromStatus = (status, winningsAmount = 0) => {
  switch (status) {
    case BetslipStatus.RUNNING:
      return {
        font: theme.History.betslip.header.running.color,
        background: theme.History.betslip.header.running.backgroundColor,
      };
    case BetslipStatus.SETTLED:
    case BetslipStatus.CASHOUT:
      return winningsAmount > 0
        ? {
            font: theme.History.betslip.header.win.color,
            background: theme.History.betslip.header.win.backgroundColor,
          }
        : {
            font: theme.History.betslip.header.lost.color,
            background: theme.History.betslip.header.lost.backgroundColor,
          };
    case BetslipStatus.CANCELLED:
    default:
      return {
        font: theme.History.betslip.header.cancelled.color,
        background: theme.History.betslip.header.cancelled.backgroundColor,
      };
  }
};

const Wrap = styled.div`
  width: 100%;
  padding: 0.34em 0.68em;
  display: flex;
  justify-content: space-between;
  color: ${theme.History.betslip.header.color};
  align-items: center;
  font-weight: 700;
`;

const BetslipType = styled.div`
  font-size: 0.92em;
`;

const BetslipDetailsWrapper = styled.div`
  display: flex;
  margin-left: 0.28em;
  gap: 0.11em;
  line-height: 1em;
  color: ${theme.History.betslip.header.detail.dotsColor};
`;

const BetslipDetail = styled.div`
  display: flex;
  align-items: center;
  align-self: center;
  gap: 0.21em;

  padding: 0 0.17em;
  border-radius: 0.46em;
  height: 1.66em;

  background: ${theme.History.betslip.header.detail.backgroundColor};
  color: ${theme.History.betslip.header.detail.color};
  font-size: 0.67em;
  font-weight: 100;
`;

const BetslipDetailNb = styled.span`
  padding-right: 0.24em;
`;

const StyledSportIcon = styled(SportIcon)`
  width: 1.66em;
  height: 1.66em;

  ${({ $cardMode }) => css`
    color: ${theme.BoostedOddCard.badge[$cardMode]?.iconColor};
  `}
`;

const StyledBetslipStatus = styled.div`
  font-size: 0.72em;
  margin-right: 0.39em;
  padding: 0.16em 0.31em;
  border-radius: 0.46em;

  ${({ $color }) => css`
    color: ${$color.font};
    background: ${$color.background};
  `};
`;
const LeftWrapper = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.2em;
`;

const RightWrapper = styled.div`
  display: flex;
  gap: 0.67em;
`;

type Props = {
  className?: string;
  betslipStatus: string;
  betslipType: string;
  betslipId: string;
  winnings?: number;
  outcomeNbBySport: { [any]: number };
  outcomeSportOrder: Array<number>;
  isSimpleBet: boolean;
  cardMode: CardMode;
};

const HistoryItemBetslipHeader = ({
  className,
  betslipStatus,
  betslipType,
  betslipId,
  winnings,
  outcomeNbBySport,
  outcomeSportOrder,
  isSimpleBet,
  cardMode,
}: Props) => {
  const getFirstSportInOutcomes = useCallback(
    (sport1, sport2) => {
      const sportId1 = parseInt(sport1);
      const sportId2 = parseInt(sport2);

      for (const sportId of outcomeSportOrder) {
        if (sportId === sportId1) {
          return -1;
        }
        if (sportId === sportId2) {
          return 1;
        }
      }

      return 0;
    },
    [outcomeSportOrder],
  );

  const outcomeNbBySportKeys = useMemo(
    () =>
      outcomeNbBySport
        ? Object.keys(outcomeNbBySport).sort(
            (key1, key2) => outcomeNbBySport[key2] - outcomeNbBySport[key1] || getFirstSportInOutcomes(key1, key2),
          )
        : null,
    [outcomeNbBySport, getFirstSportInOutcomes],
  );

  return (
    <Wrap className={className}>
      <LeftWrapper>
        <StyledBetslipStatus $color={getColorFromStatus(betslipStatus, winnings)}>
          {BetslipEntity.getStatusLabel(betslipStatus, winnings)}
        </StyledBetslipStatus>
        <BetslipType>{BetslipEntity.getTypeLabel(betslipType)}</BetslipType>
        {outcomeNbBySportKeys ? (
          <BetslipDetailsWrapper>
            {outcomeNbBySportKeys
              .slice(0, MAX_BETSLIP_DETAIL)
              .map(Number)
              .map((sportId) => (
                <BetslipDetail key={`betslip-outcome-nb-${sportId}`}>
                  <StyledSportIcon
                    sportId={sportId}
                    $cardMode={isSimpleBet && sportId === SportIds.SPECIAL_ODDS ? cardMode : null}
                  />
                  <BetslipDetailNb>{outcomeNbBySport[sportId]}</BetslipDetailNb>
                </BetslipDetail>
              ))}
            {outcomeNbBySportKeys.length > MAX_BETSLIP_DETAIL ? '...' : null}
          </BetslipDetailsWrapper>
        ) : null}
      </LeftWrapper>
      <RightWrapper>
        <BetslipOptionsModalContainer betslipId={betslipId} />
      </RightWrapper>
    </Wrap>
  );
};

export default HistoryItemBetslipHeader;
